import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

// Single source for the user's ComfyUI-service credit balance (/api/web/credits).
// Any component can call `refreshUserCredits()` (e.g. right after launching a
// corrido render) and every mounted hook instance re-fetches.

const REFRESH_EVENT = "faceshot:credits-refresh";

export function refreshUserCredits() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(REFRESH_EVENT));
  }
}

/** Per-run credit schedule from /api/web/credits. `ads` is the per-offer
 *  ladder (spot_15 … package_60) — never hardcoded in the frontend. */
export interface AdsServiceCosts {
  spot_15: number;
  spot_30: number;
  spot_60: number;
  still: number;
  package_15: number;
  package_30: number;
  package_60: number;
}

export interface ServiceCosts {
  corrido: number;
  story: number;
  ads?: AdsServiceCosts;
}

export function useUserCredits(enabled: boolean) {
  const [balance, setBalance] = useState<number | null>(null);
  const [serviceCosts, setServiceCosts] = useState<ServiceCosts | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchBalance = useCallback(async () => {
    if (!enabled) {
      setBalance(null);
      return;
    }
    setLoading(true);
    try {
      const response = await api.getCredits();
      if (response.success && response.data && Number.isFinite(response.data.balance)) {
        setBalance(response.data.balance);
        if (response.data.service_costs) setServiceCosts(response.data.service_costs);
      }
    } catch {
      // keep the last known balance; the chip shows stale data over an error
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void fetchBalance();
    if (typeof window === "undefined") return;
    const onRefresh = () => void fetchBalance();
    window.addEventListener(REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(REFRESH_EVENT, onRefresh);
  }, [fetchBalance]);

  return { balance, serviceCosts, loading, refresh: fetchBalance };
}
